import { useState, useEffect } from 'preact/hooks';

/**
 * Canonical platform detection for the mobile/desktop shell fork.
 *
 * ADAPTIVE (not merely responsive): keys off BOTH width AND pointer capability, so a
 * 900px *touch* tablet (`pointer: coarse`) stays on the mobile shell. A manual override
 * (persisted in localStorage) lets the operator force a shell.
 *
 * This is the single source of truth for shell selection — the App root switch and any
 * shell-conditional code read THIS, never an ad-hoc `matchMedia` call.
 *
 * Promoted from CodeViewer's local `useIsDesktop` (which used bare `min-width: 900px`);
 * CodeViewer is migrated onto this in Phase 0b. Fork E (locked): 900px + pointer:fine +
 * manual override. Plan: voice-assistant/plans/desktop-uiux-and-platform-isolation.md §4.3.
 */

export const SHELL_QUERY = '(min-width: 900px) and (pointer: fine)';
export const SHELL_OVERRIDE_KEY = 'va-shell-override';
export type ShellOverride = 'auto' | 'mobile' | 'desktop';

/**
 * Pure resolver — given the media-query match and a manual override, decide the shell.
 * Extracted so the decision is unit-testable without a DOM (see useIsDesktop.spec.ts).
 */
export function resolveShell(mqMatches: boolean, override: ShellOverride): boolean {
  if (override === 'desktop') return true;
  if (override === 'mobile') return false;
  return mqMatches;
}

function readOverride(): ShellOverride {
  if (typeof localStorage === 'undefined') return 'auto';
  const v = localStorage.getItem(SHELL_OVERRIDE_KEY);
  return v === 'mobile' || v === 'desktop' ? v : 'auto';
}

function currentMatch(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(SHELL_QUERY).matches;
}

/**
 * Non-hook snapshot of the shell decision, for boot/effect code outside React render
 * (e.g. useChatShell's mount anchor effect deciding whether to restore the mobile
 * drawer). Same resolver as useIsDesktop — never an ad-hoc matchMedia elsewhere.
 */
export function isDesktopShellNow(): boolean {
  return resolveShell(currentMatch(), readOverride());
}

/**
 * True when the desktop shell should render. Reactive to viewport/pointer changes and to
 * a manual override flipped in another tab. No consumers yet in Phase 0a — the App root
 * switch wires this in Phase 3 (the desktop shell is built behind it).
 */
export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    resolveShell(currentMatch(), readOverride()),
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(SHELL_QUERY);
    const recompute = () => setIsDesktop(resolveShell(mq.matches, readOverride()));
    mq.addEventListener('change', recompute);
    // pick up a manual override flipped in another tab
    window.addEventListener('storage', recompute);
    return () => {
      mq.removeEventListener('change', recompute);
      window.removeEventListener('storage', recompute);
    };
  }, []);
  return isDesktop;
}
